import { CustomForm } from '../types/CustomForm'
import {
  AnswerState,
  FieldValue,
} from '../pages/RegistrationInviteV2/context/RegistrationContext'
import { toSnakeCase } from './stringUtils'

/**
 * 🔄 restoreFormAnswers: Restores DynamicForm answers from persisted session data
 *
 * This function reverses the mapping done by mapFormAnswersObject, converting the nested
 * payload structure back to the flat AnswerState format used by DynamicForm.
 *
 * @description Used when resuming a registration session to repopulate form fields
 * with previously entered data. The function maps from the server's nested structure
 * { form: { section_key: { item_id: value } } } back to { item.id: value }.
 *
 * @param formDef - The CustomForm definition containing groups and items
 * @param persisted - The nested form data from session (data.form from API)
 * @returns AnswerState - Flat mapping of item IDs to their values for DynamicForm
 *
 * @example
 * ```typescript
 * const formDef = { groups: [{ name: "Personal Info", items: [...] }] }
 * const persisted = { personal_info: { "item_123": "John" } }
 * const restored = restoreFormAnswers(formDef, persisted)
 * // Returns: { "item_123": "John" }
 * ```
 *
 * @see mapFormAnswersObject - The inverse operation that creates nested payload
 * @since Registration v2 - Dynamic form session restoration (Updated for key-value format)
 */
export const restoreFormAnswers = (
  formDef: CustomForm,
  persisted: Record<string, Record<string, unknown>>
): AnswerState => {
  console.log('🔄 restoreFormAnswers: Restoring form answers from session', {
    formId: formDef.id,
    persistedSections: Object.keys(persisted).length,
    totalGroups: formDef.groups.length,
  })

  const result: AnswerState = {}
  let restoredCount = 0

  // Iterate through every group in the form definition
  formDef.groups.forEach((group) => {
    const sectionKey = toSnakeCase(group.name) // e.g. "Personal Info" → "personal_info"

    // Skip if this section wasn't persisted
    if (!persisted[sectionKey]) {
      console.log(
        '🔄 restoreFormAnswers: Section not found in persisted data',
        {
          groupName: group.name,
          sectionKey,
          availableSections: Object.keys(persisted),
        }
      )
      return
    }

    const sectionData = persisted[sectionKey]

    // Iterate through every item in this group
    group.items.forEach((item) => {
      // NEW: Use item.id directly as the key (no more snake_case conversion)
      const itemId = item.id

      // Check if this field has persisted data using item.id as key
      if (sectionData[itemId] !== undefined) {
        result[item.id] = sectionData[itemId] as FieldValue
        restoredCount++

        console.log('🔄 restoreFormAnswers: Restored field value', {
          itemId: item.id,
          itemName: item.name,
          sectionKey,
          value: sectionData[itemId],
          valueType: typeof sectionData[itemId],
        })
      } else {
        console.log('🔄 restoreFormAnswers: Field not found in section data', {
          itemId: item.id,
          itemName: item.name,
          sectionKey,
          availableFields: Object.keys(sectionData),
        })
      }
    })
  })

  console.log('🔄 restoreFormAnswers: Restoration complete', {
    totalFieldsRestored: restoredCount,
    resultKeys: Object.keys(result).length,
    result,
  })

  return result
}
